/**
 * BasicProfile — the plain member's profile (basic-user split, UAT 2026-09-11).
 *
 * A member who has not opted into the talent surface sees identity and ONE
 * switch. No skills, no availability, no preferred focus areas — those are
 * talent concepts, and showing them to someone who never asked to be staffable
 * is how "why am I in a directory?" tickets get filed.
 *
 * Flipping the switch sets `talent_profile_active` — the same single flag
 * behind the Talent directory, the "a position was posted" email, and the
 * apply door (routers/applications.py). The nav and this page both re-derive
 * from it, so activation immediately reveals the talent surface.
 */

import { useState, useEffect } from 'react'
import {
  makeStyles,
  tokens,
  Body1,
  Caption1,
  Field,
  Input,
  Switch,
  Title3,
} from '@fluentui/react-components'
import { Person24Regular } from '@fluentui/react-icons'
import { useEffectiveUser } from '../../hooks/useEffectiveUser'
import { useUpdateProfile } from '../../hooks/useProfile'
import { useToast } from '../../components/ToastProvider'
import { apiErrorMessage } from '../../lib/apiError'
import { bayerColors } from '../../theme'

const useStyles = makeStyles({
  root: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: bayerColors.navy,
  },
  card: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    boxShadow: tokens.shadow2,
  },
  optIn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  optInHint: { color: tokens.colorNeutralForeground3 },
})

export function BasicProfile() {
  const styles = useStyles()
  const effectiveUser = useEffectiveUser()
  const updateProfile = useUpdateProfile()
  const { showToast } = useToast()

  // Saves on flip (mirrors the council page's switch): a single boolean with
  // no partner field to batch with. Optimistic, rolled back on error.
  const [draft, setDraft] = useState<boolean>(effectiveUser?.talent_profile_active ?? false)
  useEffect(() => {
    setDraft(effectiveUser?.talent_profile_active ?? false)
  }, [effectiveUser?.id, effectiveUser?.talent_profile_active])

  const handleToggle = (_: unknown, d: { checked: boolean }) => {
    setDraft(d.checked)
    updateProfile.mutate(
      { talent_profile_active: d.checked },
      {
        onSuccess: () => {
          if (d.checked) {
            showToast({
              title: 'Talent profile activated',
              body: 'Add your skills below — that is what council matches you on. Browse opportunities is now in your sidebar.',
            })
          }
        },
        onError: (err) => {
          setDraft(!d.checked)
          showToast({
            title: 'Could not update your profile',
            body: apiErrorMessage(err),
            intent: 'error',
          })
        },
      },
    )
  }

  return (
    <div className={styles.root} data-testid="basic-profile">
      <div className={styles.header}>
        <Person24Regular />
        <Title3>My profile</Title3>
      </div>

      <div className={styles.card}>
        <Field label="Name">
          <Input value={effectiveUser?.name ?? ''} disabled data-testid="basic-name" />
        </Field>
        <Field label="Email">
          <Input value={effectiveUser?.email ?? ''} disabled data-testid="basic-email" />
        </Field>
        <Caption1>
          Name and email come from your Bayer sign-in and cannot be edited here.
        </Caption1>
      </div>

      <div className={styles.card}>
        <div className={styles.optIn}>
          <Switch
            checked={draft}
            onChange={handleToggle}
            label="Activate my talent profile"
            data-testid="basic-talent-optin"
          />
          <Body1 className={styles.optInHint}>
            Off by default. Turn it on to list your skills, apply to open positions, and
            appear in the Talent directory.
          </Body1>
        </div>
      </div>
    </div>
  )
}
